import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const Facet = ({
  label,
  parentLabel,
  clearLabel,
  name,
  multiple,
  options,
  disabled,
  onChange,
  onClear
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  const handleClickOutside = useCallback(
    e => {
      if (!ref.current.contains(e.target)) {
        setOpen(false);
      }
    },
    [ref.current]
  );

  const focusDropdown = useCallback(
    e => {
      const inputs = [...dropdownRef.current.querySelectorAll('input')];
      inputs[0].focus();
    },
    [dropdownRef.current]
  );

  const handleChange = (name, value) => {
    if (!multiple) {
      setTimeout(() => {
        setOpen(false);
      }, 200);
    }

    onChange(name, value);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (open) {
      focusDropdown();
    }
  }, [open]);

  const selectedOptions = options
    ?.filter(option => option.selected)
    .map(option => option.label);

  return (
    <div className="facet" ref={ref}>
      <button
        type="button"
        className="facet__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={!options.length || disabled}
        onClick={() => setOpen(!open)}
      >
        {parentLabel && <span className="sr-only">{parentLabel}</span>}
        <span
          className={`facet__label${
            selectedOptions.length ? ' facet__label--selected' : ''
          }`}
        >
          {label}
        </span>
        {!!selectedOptions.length && (
          <span className="facet__selection">
            {selectedOptions.length === 1
              ? selectedOptions
              : `${selectedOptions.length} options selected`}
          </span>
        )}
      </button>
      <div className="facet__dropdown" ref={dropdownRef}>
        <ul
          className="facet__dropdown-list"
          role="group"
          aria-label={`${parentLabel || ''} ${label}`.trim()}
        >
          {options.map(option => (
            <li key={option.value} aria-selected={!!option.selected}>
              <label className={`form-${multiple ? 'checkbox' : 'radio'}`}>
                <input
                  type={multiple ? 'checkbox' : 'radio'}
                  name={name}
                  tabIndex="-1"
                  checked={!!option.selected}
                  onChange={() => handleChange(name, option.value)}
                />
                <span className="checkmark">
                  {multiple && (
                    <svg viewBox="0 0 320 320">
                      <use xlinkHref="#check" />
                    </svg>
                  )}
                </span>
                {option.label}
              </label>
            </li>
          ))}
        </ul>
        {multiple && !!selectedOptions.length && (
          <button
            type="button"
            className="link link--small facet__dropdown-clear"
            onClick={onClear}
          >
            {clearLabel}
          </button>
        )}
      </div>
    </div>
  );
};

Facet.propTypes = {
  label: PropTypes.string.isRequired,
  parentLabel: PropTypes.string,
  clearLabel: PropTypes.string,
  name: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func
};

export default Facet;
